import { Disclosure } from "@headlessui/react";
import classNames from "classnames";
import { Entry } from "contentful";
import Image from "next/image";
import Link from "next/link";
import { useRouter } from "next/router";
import { useTranslations } from "next-intl";
import { useRef } from "react";

import IconArrowRight from "@/icons/icon-arrow-right";
import Grid from "~/components/common/grid";
import { useBreakpoint } from "~/contexts/breakpoint";
import { useCountryDialog } from "~/contexts/country-dialog-context";
import Editorials from "~/types/editorials";
import accordionUtils from "~/utils/accordion-utils";
import contentfulUtils from "~/utils/contentful-utils";
import crossCountryUtils from "~/utils/cross-country-utils";
import { assertEditorialType, isActiveEntry } from "~/utils/editorial-utils";
import textUtils from "~/utils/text-utils";

import Container from "../common/container";
import EscapeNewLine from "../common/escape-new-line";
import HighlightedText from "../common/highlighted-text";
import Icon from "../common/icon";
import LinkWrapper from "../common/link-wrapper";
import MediaAsset from "../common/media-asset";
import RichText from "../common/rich-text";
import styles from "./footer.module.scss";

type Props = {
  entry: Entry<unknown>;
};

export default function Footer(props: Props) {
  const breakpoint = useBreakpoint();
  const t = useTranslations();
  const router = useRouter();
  const { open } = useCountryDialog();

  assertEditorialType<Editorials.Footer>(props.entry, "footer");

  const { title, subtitle, description, media, link, accordions, socialLinks, appleAppStoreLink, googlePlayStoreLink } =
    props.entry.fields;

  const refAccordions = useRef<HTMLButtonElement[]>([]);

  const languageCode = crossCountryUtils.getCurrentLanguageCode(router);
  const countryCode = crossCountryUtils.getCurrentCountryCode(router);

  const inspectorMode = contentfulUtils.useInspectorMode(props.entry);
  const inspectorModeFooterLink = contentfulUtils.useInspectorMode(link);
  const inspectorModeAppleAppStoreLink = contentfulUtils.useInspectorMode(appleAppStoreLink);
  const inspectorModeGooglePlayStoreLink = contentfulUtils.useInspectorMode(googlePlayStoreLink);

  return (
    <footer className={styles.footer}>
      <div className={styles.footerContainer}>
        <Container>
          <Grid>
            {media && link && isActiveEntry(link) && (
              <Link
                prefetch={false}
                className={styles.loyaltyProgramImage}
                href={textUtils.sanitizeContentfulUrl(link, router)}
                target={link.fields.openOnANewTab ? "_blank" : undefined}
                {...inspectorMode?.getProps("loyaltyProgramMedia")}
              >
                <MediaAsset entry={media} className={styles.image} fill />
              </Link>
            )}
            <div className={styles.loyaltyProgramContainer}>
              {title && (
                <div className={styles.title} {...inspectorMode?.getProps("title")}>
                  <HighlightedText text={title} />
                </div>
              )}
              {subtitle && (
                <div className={styles.subtitle} {...inspectorMode?.getProps("subtitle")}>
                  <EscapeNewLine text={subtitle} />
                </div>
              )}
              {description && (
                <div className={styles.description} {...inspectorMode?.getProps("description")}>
                  <EscapeNewLine text={description} />
                </div>
              )}
              {link && isActiveEntry(link) && (
                <div className={styles.loyaltyProgramLink}>
                  <LinkWrapper
                    prefetch={false}
                    href={textUtils.sanitizeContentfulUrl(link, router)}
                    target={link.fields.openOnANewTab ? "_blank" : undefined}
                    {...inspectorModeFooterLink?.getProps("text")}
                    lookAsButton="secondary"
                  >
                    {link.fields.text}
                  </LinkWrapper>
                </div>
              )}
            </div>

            {accordions?.filter(isActiveEntry).map((accordion, index) => {
              const inspectorModeAccordion = contentfulUtils.useInspectorMode(accordion);

              return (
                <Disclosure
                  as="div"
                  key={accordion.sys.id}
                  className={classNames(styles.accordion, styles[`accordion${index + 1}`])}
                  style={{ gridRow: index === 0 && breakpoint === "desktop" ? 1 : undefined }}
                >
                  {({ open }) => (
                    <>
                      <Disclosure.Button
                        as={breakpoint === "desktop" ? "div" : "button"}
                        ref={(ref) => {
                          if (refAccordions.current && ref) {
                            refAccordions.current[index] = ref;
                          }
                        }}
                        onClick={() =>
                          accordionUtils.handleClosingDisclosures(refAccordions.current[index], refAccordions)
                        }
                        className={styles.accordionButton}
                        {...inspectorModeAccordion?.getProps("title")}
                      >
                        <div className={styles.trigger}>
                          {accordion.fields.icon && isActiveEntry(accordion.fields.icon) ? (
                            <Icon
                              svgMedia={accordion.fields.icon.fields.svgMedia}
                              name={accordion.fields.icon.fields.svgIconId}
                              aria-hidden
                            />
                          ) : null}
                          {accordion.fields.title}
                        </div>
                        <Icon
                          className={styles.mobileOnly}
                          name={open ? "chevron-up" : "chevron-down"}
                          aria-hidden
                          width={16}
                          height={16}
                        />
                      </Disclosure.Button>
                      <Disclosure.Panel static className={styles.panel}>
                        <div className={styles.panelInner}>
                          <div className={styles.richTextContainer} {...inspectorModeAccordion?.getProps("content")}>
                            {<RichText text={accordion.fields.content} />}
                          </div>
                        </div>
                      </Disclosure.Panel>
                    </>
                  )}
                </Disclosure>
              );
            })}
            <div className={styles.socialLinksContainer}>
              <span>{t("generic.follow_us_on")}</span>
              <div className={styles.socialLinks}>
                {socialLinks?.filter(isActiveEntry).map((socialLink, i) => {
                  const { icon, openOnANewTab, text } = socialLink.fields;
                  if (!isActiveEntry(icon!)) return null;
                  const inspectorModeSocialLink = contentfulUtils.useInspectorMode(socialLink);

                  const { svgIconId, svgMedia } = icon?.fields!;
                  return (
                    <Link
                      prefetch={false}
                      key={`${socialLink.sys.id}-footer-${i}`}
                      href={textUtils.sanitizeContentfulUrl(socialLink, router)}
                      target={openOnANewTab ? "_blank" : undefined}
                      {...inspectorModeSocialLink?.getProps("url")}
                      aria-label={String(icon?.fields.svgIconId)}
                    >
                      <Icon svgMedia={svgMedia} name={svgIconId} aria-hidden="true" />
                    </Link>
                  );
                })}
              </div>
            </div>
            {appleAppStoreLink || googlePlayStoreLink ? (
              <div className={styles.downloadContainer}>
                {appleAppStoreLink && appleAppStoreLink.fields?.image && isActiveEntry(appleAppStoreLink) && (
                  <Link
                    prefetch={false}
                    className={styles.goToStore}
                    href={textUtils.sanitizeContentfulUrl(appleAppStoreLink, router)}
                    target={appleAppStoreLink.fields.openOnANewTab ? "_blank" : undefined}
                    {...inspectorModeAppleAppStoreLink?.getProps("url")}
                  >
                    <MediaAsset entry={appleAppStoreLink.fields.image} width={96} height={32} />
                  </Link>
                )}
                {googlePlayStoreLink && googlePlayStoreLink.fields?.image && isActiveEntry(googlePlayStoreLink) && (
                  <Link
                    prefetch={false}
                    className={styles.goToStore}
                    href={textUtils.sanitizeContentfulUrl(googlePlayStoreLink, router)}
                    target={googlePlayStoreLink.fields.openOnANewTab ? "_blank" : undefined}
                    {...inspectorModeGooglePlayStoreLink?.getProps("url")}
                  >
                    <MediaAsset entry={googlePlayStoreLink.fields.image} width={96} height={32} />
                  </Link>
                )}
              </div>
            ) : null}

            <div className={styles.countryContainer}>
              <div className={styles.countryName}>
                <span className={styles.fontBase}>{t("generic.country")}</span>
                <span className={styles.fontHighlighted}>{countryCode}</span>
                <Image
                  unoptimized
                  width={16}
                  height={16}
                  alt={countryCode}
                  src={`/next/assets/flags/${countryCode.toLowerCase()}.svg`}
                />
              </div>
              <div className={styles.countryLanguage}>
                <span className={styles.fontBase}>{t("generic.language")}</span>
                <span className={styles.fontHighlighted}>{languageCode}</span>
              </div>
              <button onClick={open} className={styles.countryChange}>
                <span className={styles.fontBase}>{t("generic.change")}</span>
                <Icon icon={IconArrowRight} width={16} height={16} aria-hidden />
              </button>
            </div>
          </Grid>
        </Container>
      </div>
      <div className={styles.legalContainer}>
        <p className={styles.legalText}>{t("generic.footer_legal_text")}</p>
      </div>
    </footer>
  );
}
